import React, { useState, useEffect, useContext } from "react";
import { PatientContext } from '../../lib/main-context';
import { useTranslation } from "../../i18n/TranslationProvider";

// bootstrap
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import NavDropdown from "react-bootstrap/NavDropdown";

// routing
import { Link, useLocation, useHistory } from "react-router-dom";

// component
import OnboardingProgress from "./OnboardingProgress";

// api
import { isFollowUpFormDue } from "../../api/TBIRequests";
import { getPatientExercises } from "../../api/adminRequests";

const authenticatedOptions = (atPatientControlPage, userType, showFollowUpForm, t, onVideoClick) => (
  <>
    {!atPatientControlPage && (
      <Nav.Link eventKey="/home" as={Link} to="/">
        {t("header.navbar.home")}
      </Nav.Link>
    )}
    {!atPatientControlPage && userType === 'provider' &&
      <Nav.Link eventKey="/charts" as={Link} to="/charts">
        {t("header.navbar.report")}
      </Nav.Link>
    }
    {!atPatientControlPage && userType !== 'provider' && showFollowUpForm &&
      <Nav.Link eventKey="/followupform" as={Link} to="/followupform">
        {t("header.navbar.followUpForm")}
      </Nav.Link>
    }
    
    {!atPatientControlPage && userType === 'provider' &&
      <Nav.Link eventKey="/caseReport" as={Link} to="/caseReport">
        {t("header.navbar.caseReport")}
      </Nav.Link>
    }

    {!atPatientControlPage && (
      <Nav.Link eventKey="/video" onClick={onVideoClick} style={{ cursor: 'pointer' }}>
        {t("header.navbar.video")}
      </Nav.Link>
    )}

    {/* {userType === 'provider' && ( */}
      <Nav.Link eventKey="/resources" as={Link} to="/resources">
        {t("header.navbar.resources")}
      </Nav.Link>
    {/* )} */}
  </>
);

const userDropDownItems = (userType, t) => (
  <>
    <NavDropdown.Item as={Link} to="/signOut">
      {t("header.navbar.logout")}
    </NavDropdown.Item>
    {userType != "provider" && (
      <NavDropdown.Item as={Link} to="/accountSetting/ChangeNotification">
        {t("header.navbar.accountSettings")}
      </NavDropdown.Item>
    )}
  </>
);

const unauthenticatedOptions = (t) => (
  <>
    <Nav.Link eventKey="/BasicInfo" as={Link} to="/BasicInfo">
      {t("header.navbar.register")}
    </Nav.Link>
    <Nav.Link eventKey="/signIn" as={Link} to="/signIn">
      {t("header.navbar.signin")}
    </Nav.Link>
  </>
);

const Header = ({ user, routeGroup, onboardingPercent }) => {
  const { patient } = useContext(PatientContext);
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();

  const [showFollowUpForm, setShowFollowUpForm] = useState(false);
  const [assignedExercises, setAssignedExercises] = useState({});

  useEffect(() => {
    const checkFollowUpFormDue = async () => {
      if (user?.userType !== "provider" && patient?.patientId) {
        try {
          const res = await isFollowUpFormDue({ patientId: patient.patientId });
          setShowFollowUpForm(res.data.isDue);
        } catch (err) {
          console.error("Failed to check follow up form status:", err);
        }
      }
    }

    checkFollowUpFormDue();
  }, [user, patient])

  // Fetch assigned exercises so the Video tab can apply gray-out logic
  useEffect(() => {
    if (user?.userType !== "provider" && patient?.patientId) {
      getPatientExercises(patient.patientId)
        .then(({ data }) => setAssignedExercises(data || {}))
        .catch(err => console.error("Header: failed to fetch exercises:", err));
    }
  }, [user, patient]);

  // Mirrors getVideoIdForExercise in Dashboard.js
  const getVideoIdForExercise = (exercise) => {
    // Use level_since_last_eval (original assigned level) to anchor the +2 window,
    // matching Dashboard.js logic exactly.
    const level = exercise.level_since_last_eval ?? exercise.exercise_level;
    const slug = exercise.slug;
    const videoMap = {
      'palmergrasp':      { group: `pr-levels-${level}`,  item: `pg-${level}` },
      'rooting':          { group: `pr-levels-${level}`,  item: `root-${level}` },
      'snout':            { group: `pr-levels-${level}`,  item: `snout-${level}` },
      'babinski':         { group: `pr-levels-${level}`,  item: `bab-${level}` },
      'spinalgalant':     { group: `pr-levels-${level}`,  item: `sg-${level}` },
      'atnr':             { group: `pr-levels-${level}`,  item: `atnr-${level}` },
      'stnr':             { group: `pr-levels-${level}`,  item: `stnr-${level}` },
      'moro':             { group: `pr-levels-${level}`,  item: `moro-${level}` },
      'tlr':              { group: `pr-levels-${level}`,  item: `tlr-${level}` },
      'balance':          { group: 'balance',              item: `bal-${level}` },
      'curlups':          { group: `core-level-${level}`, item: `cu-${level}` },
      'lateralsideplank': { group: `core-level-${level}`, item: `lsp-${level}` },
      'pronebridge':      { group: `core-level-${level}`, item: `pb-${level}` },
      'pullups':          { group: `core-level-${level}`, item: `pu-${level}` },
      'pushups':          { group: `core-level-${level}`, item: `psh-${level}` },
      'supinebridge':     { group: `core-level-${level}`, item: `sb-${level}` },
      'oculomotor':       { group: 'oculomotor',           item: `ocul-${level}` },
      'olfactory':        { group: 'olfactory',            item: `olf-${level}` },
      'vestibular':       { group: 'vestibular',           item: `vest-${level}` },
      'interactivemetronome': { group: 'interactive-metronome', item: `int-metro-${level}` },
      'metrotimer':       { group: 'metrotimer',           item: `metro-${level}` },
      'transcranial':     { group: 'transcranial',         item: `trans-${level}` },
      'pons':             { group: 'pons',                 item: `pons-${level}` },
    };
    if (slug === 'photobiomodulation-left' || slug === 'photobiomodulation-right') {
      const prefix = slug === 'photobiomodulation-left' ? 'left' : 'right';
      const group  = slug === 'photobiomodulation-left' ? 'left-hemisphere' : 'right-hemisphere';
      let item;
      if (level === 1)                   item = `${prefix}-1`;
      else if (level >= 2 && level <= 4) item = `${prefix}-24`;
      else if (level >= 5 && level <= 7) item = `${prefix}-57`;
      else if (level >= 8 && level <= 9) item = `${prefix}-89`;
      else if (level === 10)             item = `${prefix}-10`;
      return item ? `${group}-${item}` : null;
    }
    const mapping = videoMap[slug];
    return mapping ? `${mapping.group}-${mapping.item}` : null;
  };

  const handleVideoClick = (e) => {
    e.preventDefault();
    // Providers can see all videos — skip gray-out logic entirely
    if (user?.userType === 'provider') {
      history.push('/video');
      return;
    }
    const flattened = [];
    Object.entries(assignedExercises).forEach(([categoryOrSlug, value]) => {
      if (value && typeof value === 'object' && value.exercise_name) {
        flattened.push({ slug: categoryOrSlug, ...value });
      } else if (value && typeof value === 'object') {
        Object.entries(value).forEach(([slug, exercise]) => {
          flattened.push({ slug, ...exercise });
        });
      }
    });
    const ids = flattened.map(ex => getVideoIdForExercise(ex)).filter(Boolean);
    if (ids.length > 0) {
      history.push(`/video?ids=${ids.join(',')}`);
    } else {
      history.push('/video');
    }
  };

  if (routeGroup === "onboarding" || routeGroup === "laterSymptoms")
    return (
      <>
        <Navbar
          style={{ marginBottom: "50px" }}
          id="nav-border"
          collapseOnSelect
          expand="md"
        >
          <Nav style={{ width: "100%" }}>
            <Nav.Item
              onboardingPercent={onboardingPercent}
              as={OnboardingProgress}
            />
          </Nav>
        </Navbar>
        <div className="d-flex justify-content-end">
          <Nav.Link
            style={{ marginTop: "-40px" }}
            eventKey="/logout"
            as={Link}
            to="/signOut"
          >
            {t("header.navbar.logout")}
          </Nav.Link>
          <Nav.Link
            style={{ marginTop: "-40px" }}
            eventKey="/help"
            as={Link}
            to="/help"
          >
            {t("header.navbar.help")}
          </Nav.Link>
        </div>
      </>
    );
  return (
    <>
      <Navbar
        style={{ marginBottom: "50px" }}
        id="nav-border"
        collapseOnSelect
        expand="md"
      >
        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav" style={{ width: "100%" }}>
          {routeGroup !== "onboarding" && (
            <>
              <Nav
                activeKey={location.pathname.slice(
                  location.pathname.lastIndexOf("/")
                )}
                className="mr-auto"
                style={{ fontWeight: "bold", fontSize: "120%" }}
              >
                {user
                  ? authenticatedOptions(
                    location.pathname === "/patientControl",
                    user.userType,
                    showFollowUpForm,
                    t,
                    handleVideoClick
                  )
                  : unauthenticatedOptions(t)}
                <Nav.Link eventKey="/help" as={Link} to="/help">
                  {t("header.navbar.help")}
                </Nav.Link>
              </Nav>
              <Nav
                activeKey={location.pathname.slice(
                  location.pathname.lastIndexOf("/")
                )}
                className="ml-auto"
                style={{ fontWeight: "bold", fontSize: "120%" }}
              >
                {user ? (
                  <NavDropdown
                    id="user-selection"
                    title={`${user.firstName} ${user.lastName.slice(0, 1)}.`}
                  >
                    {userDropDownItems(user.userType, t)}
                  </NavDropdown>
                ) : (
                  ""
                )}
              </Nav>
            </>
          )}
        </Navbar.Collapse>
      </Navbar>
    </>
  );
};
export default Header;